import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    ShoppingBag, ShieldCheck, Truck, Sparkle, BagSimple, Drop, BowlFood,
    Baseball, Moon, Wrench, Stack, Infinity as InfinityIcon
} from "@phosphor-icons/react";

const CATEGORIES = [
    { id: "all", label: "All", icon: Stack },
    { id: "cases", label: "Cases", icon: BagSimple },
    { id: "cleaning", label: "Cleaning", icon: Drop },
    { id: "guards", label: "Guards", icon: ShieldCheck },
    { id: "retainers", label: "Retainers", icon: InfinityIcon },
    { id: "tools", label: "Tools", icon: Wrench },
];

const PRODUCTS = [
    // CASES
    {
        id: "case-mango-coral",
        category: "cases",
        name: "Aligner Case, Mango Coral",
        desc: "Vivid coral-orange shell with anti-slip lining. Pocket-friendly and easy to spot.",
        price: 1299,
        image: "/marketing/shop-1.png",
    },
    {
        id: "case-duo",
        category: "cases",
        name: "Aligner Case, Duo Pack (Navy + Coral)",
        desc: "His-and-hers twin pack. One midnight navy, one mango coral, share the journey.",
        price: 2299,
        image: "/marketing/shop-2.png",
        featured: true,
    },
    {
        id: "case-sport-coral",
        category: "cases",
        name: "Aligner Case, Sport Coral",
        desc: "Reinforced coral case with carabiner loop, safe during gym, travel, and sports.",
        price: 1499,
        image: "/marketing/shop-3.png",
    },
    {
        id: "case-midnight",
        category: "cases",
        name: "Aligner Case, Midnight Blue",
        desc: "Signature deep-navy shell with magnetic close. Understated and pocket-friendly.",
        price: 1499,
        image: "/marketing/shop-4.png",
    },
    {
        id: "case-pearl",
        category: "cases",
        name: "Aligner Case, Pearl White",
        desc: "Soft-touch matte white finish with the Invisique™ mark. Minimal, clinical, beautiful.",
        price: 1499,
        image: "/marketing/shop-5.png",
    },
    // CLEANING
    {
        id: "cleaning-crystals",
        category: "cleaning",
        name: "Invisique™ Cleaning Crystals+",
        desc: "Professional-grade effervescent crystals. Dissolves plaque in 15 minutes.",
        price: 2999,
        image: "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?auto=format&fit=crop&w=600&q=60",
    },
    {
        id: "cleaning-tablets",
        category: "cleaning",
        name: "Cleaning Tablets (30 pack)",
        desc: "Daily-use fizzing tablets that freshen aligners and retainers.",
        price: 699,
        image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=600&q=60",
    },
    {
        id: "ultrasonic-cleaner",
        category: "cleaning",
        name: "Ultrasonic Cleaner",
        desc: "Personal ultrasonic bath, deep cleans in 3 minutes. Includes personalised case.",
        price: 6499,
        image: "/marketing/ultrasonic-cleaner.jpg",
    },
    {
        id: "cleaning-spray",
        category: "cleaning",
        name: "On-the-Go Cleaning Spray",
        desc: "Travel-friendly spray bottle. Rinse, spray, and reinsert, no water needed.",
        price: 549,
        image: "https://images.unsplash.com/photo-1611930022073-b7a4ba5fcccd?auto=format&fit=crop&w=600&q=60",
    },
    // GUARDS
    {
        id: "night-guard",
        category: "guards",
        name: "Custom Night Guard",
        desc: "Doctor-prescribed guard for bruxism (teeth grinding). Ships to your clinic.",
        price: 8999,
        image: "https://images.pexels.com/photos/3845126/pexels-photo-3845126.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=500",
    },
    {
        id: "sports-guard",
        category: "guards",
        name: "Custom Sports Guard",
        desc: "Impact-resistant, low-profile sports mouthguard. Breathe easy, talk easy.",
        price: 7499,
        image: "https://images.pexels.com/photos/221210/pexels-photo-221210.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=500",
    },
    {
        id: "whitening-guard",
        category: "guards",
        name: "Whitening Tray + Gel Kit",
        desc: "Custom-fit whitening trays with professional-grade gel. Use 20 min/day.",
        price: 4999,
        image: "https://images.unsplash.com/photo-1606265752439-1f18756aa5fc?auto=format&fit=crop&w=600&q=60",
    },
    // RETAINERS
    {
        id: "infinite-retainer",
        category: "retainers",
        name: "Invisique™ Infinite™, 1 Set",
        desc: "Premium retainer set. Crystal clear, multi-layer polymer construction, laser-cut to the gumline.",
        price: 16999,
        image: "/marketing/invisique-infinite-retainers.png",
        featured: true,
    },
    {
        id: "infinite-plan",
        category: "retainers",
        name: "Invisique™ Infinite™ Plan, 4 Sets",
        desc: "Best value. 4 replacement sets + lifetime digital archiving + priority shipping.",
        price: 39999,
        image: "https://images.pexels.com/photos/6528856/pexels-photo-6528856.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=500",
        featured: true,
    },
    {
        id: "standard-retainer",
        category: "retainers",
        name: "Standard Clear Retainer",
        desc: "Entry-level retainer with standard polymer. Upper & lower set.",
        price: 7999,
        image: "https://images.pexels.com/photos/6528960/pexels-photo-6528960.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=500",
    },
    // TOOLS
    {
        id: "chewies",
        category: "tools",
        name: "Aligner Chewies (pack of 4)",
        desc: "Soft silicone chewies, help aligners seat perfectly every time.",
        price: 449,
        image: "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?auto=format&fit=crop&w=600&q=60",
    },
    {
        id: "removal-tool",
        category: "tools",
        name: "Aligner Removal Tool",
        desc: "Hook-style tool that lifts aligners off cleanly, protecting your nails.",
        price: 299,
        image: "https://images.unsplash.com/photo-1611930022073-b7a4ba5fcccd?auto=format&fit=crop&w=600&q=60",
    },
    {
        id: "travel-kit",
        category: "tools",
        name: "Travel Care Kit",
        desc: "Bundle: sports case + removal tool + 10 cleaning tablets + chewies.",
        price: 1999,
        image: "https://images.pexels.com/photos/6528869/pexels-photo-6528869.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=500",
    },
];

export default function Shop() {
    const [cat, setCat] = useState("all");
    const items = useMemo(
        () => (cat === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === cat)),
        [cat]
    );

    return (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 lg:py-20" data-testid="shop-page">
            <div className="grid lg:grid-cols-12 gap-8 items-end mb-10">
                <div className="lg:col-span-7">
                    <div className="overline text-primary mb-3">Official accessories</div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                        Keep your smile journey smooth.
                    </h1>
                </div>
                <p className="lg:col-span-5 text-muted-foreground">
                    Doctor-recommended accessories for aligner and retainer wearers, from cleaning crystals
                    to night guards. Free shipping over ₹999.
                </p>
            </div>

            <div className="flex flex-wrap gap-6 mb-10 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><Truck size={16} weight="duotone" /> Free shipping over ₹999</div>
                <div className="flex items-center gap-2"><ShieldCheck size={16} weight="duotone" /> 30-day returns</div>
                <div className="flex items-center gap-2"><Sparkle size={16} weight="duotone" /> Doctor-recommended</div>
            </div>

            {/* Category filter */}
            <div className="flex flex-wrap gap-2 mb-10 sticky top-16 lg:top-20 bg-background/90 backdrop-blur py-3 z-30" data-testid="shop-categories">
                {CATEGORIES.map((c) => (
                    <button
                        key={c.id}
                        onClick={() => setCat(c.id)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                            cat === c.id
                                ? "bg-primary text-white border-primary"
                                : "bg-card text-foreground border-border hover:border-primary"
                        }`}
                        data-testid={`shop-cat-${c.id}`}
                    >
                        <c.icon size={16} weight={cat === c.id ? "fill" : "duotone"} /> {c.label}
                    </button>
                ))}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((p) => (
                    <div key={p.id} className="bg-card border border-border rounded-2xl overflow-hidden bento-shadow relative" data-testid={`product-${p.id}`}>
                        {p.featured && (
                            <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-primary text-white text-[10px] tracking-[0.2em] uppercase font-semibold">
                                Featured
                            </div>
                        )}
                        <div className="aspect-square bg-secondary overflow-hidden">
                            <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="p-5">
                            <h3 className="font-display text-lg font-semibold">{p.name}</h3>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.desc}</p>
                            <div className="mt-4 flex items-center justify-between">
                                <div className="font-display text-xl font-bold">₹{p.price.toLocaleString("en-IN")}</div>
                                <Button
                                    onClick={() => toast.success(`${p.name} added to cart`)}
                                    className="rounded-full"
                                    size="sm"
                                    data-testid={`add-cart-${p.id}`}
                                >
                                    Add to cart
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {items.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">No products in this category.</div>
            )}
        </div>
    );
}
